import React from 'react'
import { AdCreativeType, CampaignConfig, SprintDTO } from '@marketing-milk/interfaces'
import { Button } from '@material-ui/core'
import { Add, Close } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { removeAtIndex } from 'app/util'
import { capitalCase } from 'change-case'
import { set, update } from 'lodash/fp'
import { getGoalColorClasses } from 'pages/business/campaigns/helpers/campaignForm.helpers'
import {
  creativeScaffoldErrorMessage,
  creativeScaffoldValidator,
} from 'pages/business/sprints/sprint.helpers'
import { NameField } from '../../sprint-campaigns-tab/Fields/CondensedCampaignNameField'
import { CreativeScaffold } from '../CreateSprintForm'

interface Props {
  campaignConfigs: CampaignConfig[]
  sprintDto: SprintDTO
  sprintCreatives: { [campaignIndex: number]: CreativeScaffold[][] }
  setSprintCreatives: SetState<{ [campaignIndex: number]: CreativeScaffold[][] }>
  defaultCreativeCount: number
  setDefaultCreativeCount: SetState<number>
}
export const SprintScaffoldCreativesStep = ({
  campaignConfigs,
  sprintCreatives,
  setSprintCreatives,
  defaultCreativeCount,
  setDefaultCreativeCount,
}: Props) => {
  // TODO: Support Cascading
  const addCreative = (campaignIndex: number) => {
    setSprintCreatives(
      update([campaignIndex, 0], (creatives: CreativeScaffold[]) => [
        ...creatives,
        {
          name: `${campaignConfigs[campaignIndex].name} - Ad ${
            campaignIndex + 1
          }${String.fromCharCode(65 + creatives.length)}`,
          type: AdCreativeType.single_image,
          campaignIndex,
          creativeGroup: 0,
        },
      ])
    )
  }

  return (
    <div className="w-full flex flex-col mx-auto p-4 space-y-4">
      <h1 className="py-2 font-lg font-bold mx-auto">Scaffold Creatives</h1>
      <p className="font-medium mx-auto max-w-2xl font-md text-gray-600">
        Scaffold out the new creatives you will use in this sprint
      </p>

      <div className="max-w-fit mx-auto mb-8 space-y-3">
        <Alert severity="info" variant="standard" className="flex items-center">
          After publishing, you will be able to add images and copy to each of these creatives
        </Alert>
        <div className="flex items-center justify-center space-x-3">
          <label className="text-sm font-medium text-gray-700">
            Default creatives per campaign
          </label>
          <div className="flex items-center border rounded overflow-hidden">
            <button
              className="px-3 py-1 text-gray-600 hover:bg-gray-100 text-lg font-medium"
              onClick={() => setDefaultCreativeCount(c => Math.max(1, c - 1))}
            >
              −
            </button>
            <span className="px-4 py-1 text-sm font-medium border-x">{defaultCreativeCount}</span>
            <button
              className="px-3 py-1 text-gray-600 hover:bg-gray-100 text-lg font-medium"
              onClick={() => setDefaultCreativeCount(c => Math.min(26, c + 1))}
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 w-full">
        {campaignConfigs.map((campaign, campaignIndex) => (
          <div className="w-full h-full border rounded-md shadow-xl">
            <div className="py-4 space-x-4 bg-gray-200 flex justify-center items-center">
              <p className="font-lg font-semibold text-gray-800">{campaign.name}</p>
              <p className={`${getGoalColorClasses(campaign.goal)} py-2 px-4 rounded-full text-sm`}>
                {campaign?.goal ? capitalCase(campaign.goal) : 'Not selected'}
              </p>
            </div>

            <div className="space-y-2 px-4 w-full h-full py-4">
              {sprintCreatives[campaignIndex]?.map((creativeGroup, groupIndex) => (
                <>
                  {creativeScaffoldValidator(creativeGroup) && (
                    <div className="w-full px-4 mx-auto">
                      <Alert severity="error" variant="standard" className="flex items-center">
                        {creativeScaffoldErrorMessage(creativeGroup)}
                      </Alert>
                    </div>
                  )}
                  {creativeGroup.map((creative, creativeIndex) => (
                    <ScaffoldCreativeForm
                      key={creative.name + creativeIndex}
                      creative={creative}
                      campaignIndex={campaignIndex}
                      groupIndex={groupIndex}
                      creativeIndex={creativeIndex}
                      setSprintCreatives={setSprintCreatives}
                    />
                  ))}
                  {!creativeGroup.map(({ type }) => type).includes(AdCreativeType.dynamic) && (
                    <div className="p-4">
                      <Button
                        variant="outlined"
                        className="w-full p-3"
                        onClick={() => addCreative(campaignIndex)}
                      >
                        <Add />
                        <p className="ml-2">Add Creative To Campaign</p>
                      </Button>
                    </div>
                  )}
                </>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

type ScaffoldCreativeFormProps = {
  creative: CreativeScaffold
  campaignIndex: number
  groupIndex: number
  creativeIndex: number
  setSprintCreatives: SetState<{ [campaignIndex: number]: CreativeScaffold[][] }>
}
const ScaffoldCreativeForm = ({
  creative,
  creativeIndex,
  campaignIndex,
  groupIndex,
  setSprintCreatives,
}: ScaffoldCreativeFormProps) => {
  return (
    <div data-testid={`creative-${creativeIndex}`} className="w-full">
      <div className="flex items-center py-4 px-2 w-full" key={creative.name + creativeIndex}>
        <div className="flex-grow min-w-0 mr-2">
          <NameField
            index={creativeIndex}
            value={creative.name}
            setValue={value =>
              setSprintCreatives(set([campaignIndex, groupIndex, creativeIndex, 'name'], value))
            }
            labelPrefix="Creative"
            inputPadding="16px 14px"
          />
        </div>

        <div className="w-40 shrink-0">
          <FormControl>
            <InputLabel htmlFor={`creative-${campaignIndex + groupIndex + creativeIndex}-type`}>
              Creative Type
            </InputLabel>
            <Select
              placeholder="Creative Type"
              labelId={`creative-${campaignIndex + groupIndex + creativeIndex}-type`}
              variant="outlined"
              name={`creative-${campaignIndex + groupIndex + creativeIndex}-type`}
              value={creative.type}
              inputProps={{
                label: 'Creative Type',
                id: `creative-${campaignIndex + groupIndex + creativeIndex}-type`,
              }}
              onChange={({ target }) =>
                setSprintCreatives(
                  set([campaignIndex, groupIndex, creativeIndex, 'type'], target.value)
                )
              }
              label={'Creative Type'}
              required={true}
              sx={{ '& .MuiSelect-select': { padding: '16px 14px' } }}
            >
              {/* A per-placement ad's cuts only come from import. */}
              {Object.values(AdCreativeType)
                .filter(type => type !== AdCreativeType.per_placement)
                .map(type => (
                  <MenuItem value={type} style={{ width: '100%' }}>
                    {capitalCase(type)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>

        <div className="ml-2 flex items-center justify-center">
          <button
            className="border border-red-900 rounded-full p-2 text-red-700 hover:bg-red-100"
            onClick={() =>
              setSprintCreatives(update([campaignIndex, groupIndex], removeAtIndex(creativeIndex)))
            }
          >
            <Close />
          </button>
        </div>
      </div>
    </div>
  )
}
